// Custom Partner Types for user-specific partners

export interface CustomPartner {
  id: number;
  name: string;
  partnerType: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  isActive: boolean;
  createdAt: string;
  userId: string;
}

export interface CreateCustomPartnerRequest {
  name: string;
  partnerType: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  isActive?: boolean;
}

export interface UpdateCustomPartnerRequest {
  name?: string;
  partnerType?: string;
  description?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  logo?: string;
  isActive?: boolean;
}

// Partner Types (same as regular partners)
export type PartnerType = 
  | 'sponsor'
  | 'supplier'
  | 'media'
  | 'venue'
  | 'service'
  | 'charity'
  | 'technology'
  | 'other';

export const PartnerTypeLabels: Record<PartnerType, string> = {
  sponsor: 'Sponsor',
  supplier: 'Lieferant',
  media: 'Medien',
  venue: 'Veranstaltungsort',
  service: 'Dienstleister',
  charity: 'Wohltätigkeit',
  technology: 'Technologie',
  other: 'Sonstiges',
};

export const PartnerTypeColors: Record<PartnerType, string> = {
  sponsor: '#10b981',
  supplier: '#3b82f6',
  media: '#ef4444',
  venue: '#f59e0b',
  service: '#06b6d4',
  charity: '#84cc16',
  technology: '#8b5cf6',
  other: '#6b7280',
};

export const PartnerTypeIcons: Record<PartnerType, string> = {
  sponsor: '',
  supplier: '',
  media: '',
  venue: '',
  service: '',
  charity: '',
  technology: '',
  other: '',
};

// Form validation
export const validateCustomPartner = (partner: CreateCustomPartnerRequest | UpdateCustomPartnerRequest): string[] => {
  const errors: string[] = [];

  if ('name' in partner && partner.name !== undefined) {
    if (!partner.name || partner.name.trim().length === 0) {
      errors.push('Name ist erforderlich');
    } else if (partner.name.length > 200) {
      errors.push('Name darf maximal 200 Zeichen lang sein');
    }
  }

  if ('partnerType' in partner && partner.partnerType !== undefined) {
    if (!partner.partnerType || partner.partnerType.trim().length === 0) {
      errors.push('Partner-Typ ist erforderlich');
    } else if (partner.partnerType.length > 50) {
      errors.push('Partner-Typ darf maximal 50 Zeichen lang sein');
    }
  }

  if (partner.description && partner.description.length > 1000) {
    errors.push('Beschreibung darf maximal 1000 Zeichen lang sein');
  }

  if (partner.website && partner.website.length > 500) {
    errors.push('Website-URL darf maximal 500 Zeichen lang sein');
  }

  if (partner.contactEmail) {
    if (partner.contactEmail.length > 200) {
      errors.push('E-Mail-Adresse darf maximal 200 Zeichen lang sein');
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(partner.contactEmail)) {
      errors.push('Ungültige E-Mail-Adresse');
    }
  }

  if (partner.contactPhone && partner.contactPhone.length > 50) {
    errors.push('Telefonnummer darf maximal 50 Zeichen lang sein');
  }

  if (partner.address && partner.address.length > 500) {
    errors.push('Adresse darf maximal 500 Zeichen lang sein');
  }

  if (partner.logo && partner.logo.length > 500) {
    errors.push('Logo-URL darf maximal 500 Zeichen lang sein');
  }

  return errors;
};

// Helper functions
export const getPartnerTypeLabel = (type: string): string => {
  return PartnerTypeLabels[type as PartnerType] || type;
};

export const getPartnerTypeColor = (type: string): string => {
  return PartnerTypeColors[type as PartnerType] || '#6b7280';
};

export const getPartnerTypeIcon = (type: string): string => {
  return PartnerTypeIcons[type as PartnerType] || '';
};

// Filter and sort helpers
export const filterCustomPartners = (
  partners: CustomPartner[],
  searchTerm: string,
  filterType: string,
  includeInactive: boolean
): CustomPartner[] => {
  return partners.filter(partner => {
    // Search filter
    const matchesSearch = !searchTerm || 
      partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (partner.description && partner.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      partner.partnerType.toLowerCase().includes(searchTerm.toLowerCase());

    // Type filter
    const matchesType = filterType === 'all' || partner.partnerType === filterType;

    // Active filter
    const matchesActive = includeInactive || partner.isActive;

    return matchesSearch && matchesType && matchesActive;
  });
};

export const sortCustomPartners = (
  partners: CustomPartner[],
  sortBy: 'name' | 'type' | 'created' = 'name',
  sortOrder: 'asc' | 'desc' = 'asc'
): CustomPartner[] => {
  return [...partners].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'type':
        comparison = a.partnerType.localeCompare(b.partnerType);
        break;
      case 'created':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });
};

// Statistics helpers
export const getCustomPartnerStats = (partners: CustomPartner[]) => {
  const total = partners.length;
  const active = partners.filter(p => p.isActive).length;
  const inactive = total - active;
  
  const typeStats = partners.reduce((acc, partner) => {
    acc[partner.partnerType] = (acc[partner.partnerType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const mostCommonType = Object.entries(typeStats)
    .sort(([,a], [,b]) => b - a)[0];

  return {
    total,
    active,
    inactive,
    typeStats,
    mostCommonType: mostCommonType ? {
      type: mostCommonType[0],
      count: mostCommonType[1],
      label: getPartnerTypeLabel(mostCommonType[0])
    } : null,
    uniqueTypes: Object.keys(typeStats).length
  };
};
